import { makeStyles } from '@material-ui/styles'
import { useEffect } from 'react'
import { qemuGuestAgentDoc } from 'app/docLinks'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import Info from 'core/components/validatedForm/Info'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import { type CreateVmFormParams, isWindowsVmSource } from '../CreateVirtualMachineModal.helpers'
import {
  cloudInitForAutoInstall,
  getBootSourceImageMetadata,
  isQemuPropertySet,
  QEMU_OPTIONAL_PROP,
  QEMU_REQUIRED_PROP,
  qemuAutoInstallApplies,
} from '../utils/qemuGuestAgent'

interface Props {
  params: CreateVmFormParams
  updateParams: (partial: Partial<CreateVmFormParams>) => void
  getVolumeSnapshotSourceVolume: (volumeSnapshot: any) => any
}

interface PropertyRowProps {
  label: string
  property: string
  isSet: boolean
}

// One "Required:/Optional: <prop>=yes  [Set|Not set]" row.
function PropertyRow({ label, property, isSet }: PropertyRowProps) {
  const classes = useStyles()
  return (
    <div className={classes.propertyRow}>
      <Text variant="body2" className={classes.propertyLabel}>
        {label}
      </Text>
      <Text variant="body2" component="code" className={classes.propertyCode}>
        {property}=yes
      </Text>
      <BadgeWithIcon
        variant={isSet ? 'success' : 'default'}
        icon={isSet ? 'check' : 'times'}
        text={isSet ? 'Set' : 'Not set'}
        hideTooltip
      />
    </div>
  )
}

// See QemuGuestAgentSection.spec.md for QGA-FR-001 through QGA-FR-010
export default function QemuGuestAgentSection({
  params,
  updateParams,
  getVolumeSnapshotSourceVolume,
}: Props) {
  const classes = useStyles()

  const metadata = getBootSourceImageMetadata(params, getVolumeSnapshotSourceVolume)
  const requiredSet = isQemuPropertySet(metadata, QEMU_REQUIRED_PROP)
  const optionalSet = isQemuPropertySet(metadata, QEMU_OPTIONAL_PROP)
  // Name the thing the properties are read from: the volume for existing volumes and volume
  // snapshots, otherwise the image (including Boot from New Volume, which copies the image).
  const bootSourceWord = ['existingVolume', 'volumeSnapshot'].includes(params.sourceType)
    ? 'volume'
    : 'image'
  const showToggle = qemuAutoInstallApplies(params, getVolumeSnapshotSourceVolume)
  const isWindows = isWindowsVmSource(params, getVolumeSnapshotSourceVolume)
  const { autoInstallQemuGuestAgent, cloudInitYaml } = params

  // Windows/ISO can't use the cloud-init install, so switch auto-install off there (and drop the
  // agent lines from the example Cloud-init if the user hasn't edited it).
  useEffect(() => {
    if (showToggle || !autoInstallQemuGuestAgent) return
    updateParams({
      autoInstallQemuGuestAgent: false,
      cloudInitYaml: cloudInitForAutoInstall(cloudInitYaml, false),
    })
  }, [showToggle, autoInstallQemuGuestAgent, cloudInitYaml, updateParams])

  // Auto-install runs through cloud-init, so turning it on also turns Use Cloud-init on.
  const handleToggle = (value: boolean) =>
    updateParams({
      autoInstallQemuGuestAgent: value,
      cloudInitYaml: cloudInitForAutoInstall(cloudInitYaml, value),
      ...(value && { useCloudInit: true }),
    })

  return (
    <div className={classes.section}>
      <Text variant="subtitle2">QEMU Guest Agent</Text>
      <Text variant="body2">
        The following properties affect the QEMU Guest Agent and must be present on the boot{' '}
        {bootSourceWord} at VM creation time.{' '}
        <ExternalLink className={classes.link} url={qemuGuestAgentDoc}>
          Learn more
        </ExternalLink>
      </Text>

      <PropertyRow label="Required:" property={QEMU_REQUIRED_PROP} isSet={requiredSet} />
      <PropertyRow label="Optional:" property={QEMU_OPTIONAL_PROP} isSet={optionalSet} />

      {showToggle ? (
        <>
          <ToggleSwitchField
            id="autoInstallQemuGuestAgent"
            value={autoInstallQemuGuestAgent}
            onChange={handleToggle}
            label="Auto-install QEMU Guest Agent via cloud-init on first boot"
            textDescription="Installs and starts qemu-guest-agent on first boot. The install lines are added to the example Cloud-init below."
          />
          {/* The toggle only adds the install lines to the untouched example, so tell the user
              when their edited Cloud-init does not include the agent. */}
          {autoInstallQemuGuestAgent && !cloudInitYaml?.includes('qemu-guest-agent') && (
            <Alert
              variant="warning"
              message={
                <Text variant="body2">
                  Your Cloud-init below was edited, so the install lines were not added. Add{' '}
                  <strong>qemu-guest-agent</strong> to <strong>packages</strong> to install it.
                </Text>
              }
            />
          )}
          {autoInstallQemuGuestAgent && !requiredSet && (
            <Alert
              variant="warning"
              message={
                <Text variant="body2">
                  The agent will be installed but stays inactive until the boot {bootSourceWord} has{' '}
                  <strong>{QEMU_REQUIRED_PROP}=yes</strong>. This property is fixed at creation, so
                  set it on the {bootSourceWord} and redeploy.
                </Text>
              }
            />
          )}
        </>
      ) : (
        isWindows && (
          <Info className={classes.info}>
            <Text variant="body2">Install QEMU Guest Agent in the guest OS after first boot.</Text>
          </Info>
        )
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  section: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  propertyRow: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr max-content',
    alignItems: 'center',
    gap: theme.spacing(2),
    background: theme.palette.grey[50],
    borderRadius: 4,
    padding: theme.spacing(1, 2),
  },
  propertyLabel: {
    fontWeight: 600,
  },
  propertyCode: {
    fontFamily: 'monospace',
  },
  info: {
    background: theme.palette.grey[50],
  },
  link: {
    marginLeft: theme.spacing(0.5),
  },
}))
